import axios, { type AxiosResponse } from 'axios'
import type {
  AssignDocumentFamiliarizersPayload,
  CreateDocumentPayload,
  Department,
  DepartmentsResponse,
  DocumentDetails,
  DocumentAudioKind,
  DocumentAudioManifest,
  DocumentText,
  DocumentEdition,
  DocumentItem,
  DocumentResponsibilityEntry,
  DocumentStatusSlug,
  DocumentsResponse,
  FetchDocumentsParams,
  PositionsResponse,
  UpdateDocumentPayload,
} from '../types/document.types'
import { getApiTreeChildren } from '../utils/normalizeApiTree'
import { BASE_URL } from '@/environment/environment'

const API_BASE = `${BASE_URL}/api/documents`
const API_EMPLOYEE = `${BASE_URL}/api/employee`

const ARRAY_PARAMS = ['types', 'scopes', 'categories', 'created_by', 'authors', 'status', 'sections'] as const

function toQueryParams(p?: FetchDocumentsParams): Record<string, string | number> {
  if (!p) {
    return {}
  }
  const out: Record<string, string | number> = {}
  for (const [k, v] of Object.entries(p)) {
    if (v === undefined || v === null) {
      continue
    }
    if (ARRAY_PARAMS.includes(k as any) && Array.isArray(v)) {
      if (v.length) {
        out[k] = v.join(',')
      }
    } else if (typeof v === 'boolean') {
      out[k] = String(v)
    } else {
      out[k] = v as string | number
    }
  }

  return out
}

type RawDepartment = Omit<Department, 'children'> & {
  children?: RawDepartment[]
  childrens?: RawDepartment[]
}

function normalizeDepartment(item: RawDepartment): Department {
  return {
    id: item.id,
    name: item.name,
    short_name: item.short_name,
    children: getApiTreeChildren(item).map(normalizeDepartment),
  }
}

function normalizeDepartments(items: RawDepartment[]): Department[] {
  return items.map(normalizeDepartment)
}

export class DocumentsApi {
  static async fetchText(documentId: string, kind: DocumentAudioKind, signal?: AbortSignal): Promise<DocumentText> {
    const { data } = await axios.get<DocumentText>(`${API_BASE}/document/${documentId}/texts/`, { params: { kind }, signal })

    return data
  }

  static async fetchAudio(documentId: string): Promise<DocumentAudioManifest> {
    const { data } = await axios.get<DocumentAudioManifest>(`${API_BASE}/document/${documentId}/audio/`)

    return data
  }

  static async prepareAudio(documentId: string, kind: DocumentAudioKind): Promise<DocumentAudioManifest> {
    const { data } = await axios.post<DocumentAudioManifest>(`${API_BASE}/document/${documentId}/audio/`, { kind })

    return data
  }

  /** Получает список документов с фильтрами и пагинацией. Массивы (types, scopes, categories, created_by) передаются как строки через запятую. */
  static async fetchDocuments(params?: FetchDocumentsParams): Promise<DocumentsResponse> {
    const { data } = await axios.get<DocumentsResponse>(`${API_BASE}/document/`, { params: toQueryParams(params) })

    return data
  }

  /** Получает детали документа по ID (для формы) */
  static async fetchDocument(documentId: string): Promise<DocumentItem> {
    const { data } = await axios.get<DocumentItem>(`${API_BASE}/document/${documentId}/`)

    return data
  }

  /** Детали документа с историей версий */
  static async fetchDocumentDetails(documentId: string): Promise<DocumentDetails> {
    const { data } = await axios.get<DocumentDetails>(`${API_BASE}/document/${documentId}/`)

    return { ...data, versions: data.versions || [] }
  }

  /** Добавляет новую редакцию документа */
  static async addDocumentVersion(documentId: string, formData: FormData): Promise<DocumentEdition> {
    const { data } = await axios.post<DocumentEdition>(`${API_BASE}/document/${documentId}/versions/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })

    return data
  }

  /** Обновляет метаданные текущей редакции документа */
  static async updateDocumentLastVersion(documentId: string, formData: FormData): Promise<DocumentEdition> {
    const { data } = await axios.patch<DocumentEdition>(`${API_BASE}/document/${documentId}/last-version/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })

    return data
  }

  /** Меняет статус документа (архив / действует) */
  static async changeDocumentStatus(documentId: string, newStatus: Extract<DocumentStatusSlug, 'active' | 'archived'>): Promise<DocumentDetails> {
    const { data } = await axios.post<DocumentDetails>(`${API_BASE}/document/${documentId}/status/`, {
      new_status: newStatus,
    })

    return data
  }

  /** Создает новый документ (JSON). Файл — через addDocumentVersion после создания. */
  static async createDocument(payload: CreateDocumentPayload): Promise<DocumentItem> {
    const { data } = await axios.post<DocumentItem>(`${API_BASE}/document/`, payload, {
      headers: { 'Content-Type': 'application/json' },
    })

    return data
  }

  /** Обновляет документ (JSON: global_access, employee_access и др.) */
  static async updateDocument(documentId: string, payload: UpdateDocumentPayload): Promise<DocumentItem> {
    const { data } = await axios.patch<DocumentItem>(`${API_BASE}/document/${documentId}/`, payload)

    return data
  }

  static async deleteDocuments(documentIds: string | string[]): Promise<void> {
    const ids = Array.isArray(documentIds) ? documentIds : [documentIds]
    if (ids.length === 0) {
      return
    }

    if (ids.length === 1) {
      await axios.delete(`${API_BASE}/document/${ids[0]}/`)

      return
    }

    await axios.delete(`${API_BASE}/document/bulk-destroy/`, {
      params: { ids: ids.join(',') },
    })
  }

  /** Скачивает документы и папки (один файл или zip-архив с несколькими) */
  static async downloadDocuments(
    documentIds: string[],
    folderIds: string[] = [],
  ): Promise<AxiosResponse<Blob>> {
    const body: Record<string, string[]> = {}

    if (documentIds.length > 0) {
      body.document_ids = documentIds
    }
    if (folderIds.length > 0) {
      body.folder_ids = folderIds
    }

    return axios.post(
      `${API_BASE}/document/download/`,
      body,
      { responseType: 'blob' },
    )
  }

  /** Список всех подразделений больницы */
  static async fetchDepartments(): Promise<DepartmentsResponse> {
    const { data } = await axios.get<DepartmentsResponse>(`${API_EMPLOYEE}/department/`)

    return {
      ...data,
      results: normalizeDepartments(data.results),
    }
  }

  /** Список должностей (с пагинацией по cursor) */
  static async fetchPositions(params?: { cursor?: string }): Promise<PositionsResponse> {
    const queryParams: Record<string, string | number> = { page_size: 999 }
    if (params?.cursor) {
      queryParams.cursor = params.cursor
    }

    const { data } = await axios.get<PositionsResponse>(`${API_EMPLOYEE}/position/`, {
      params: queryParams,
    })

    return data
  }

  /** Перемещает документы и папки в другую папку */
  static async moveDocuments(documentIds: string[], folderIds: string[], targetFolderId: string): Promise<{ moved: number; folder_id: string }> {
    const { data } = await axios.post(`${API_BASE}/document/transfer/`, {
      document_ids: documentIds,
      folder_ids: folderIds,
      trasfer_to_folder_id: targetFolderId,
    })

    return data
  }

  /** Список записей ознакомления по документу */
  static async fetchDocumentFamiliarizers(
    documentId: string,
  ): Promise<DocumentResponsibilityEntry[] | { results: DocumentResponsibilityEntry[] }> {
    const { data } = await axios.get<
      DocumentResponsibilityEntry[] | { results: DocumentResponsibilityEntry[] }
    >(`${API_BASE}/document/${documentId}/responsibility-entries/`)

    return data
  }

  /** Назначить сотрудников на ознакомление */
  static async assignDocumentFamiliarizers(
    documentId: string,
    payload: AssignDocumentFamiliarizersPayload,
  ): Promise<DocumentResponsibilityEntry[] | { results: DocumentResponsibilityEntry[] }> {
    const { data } = await axios.post<
      DocumentResponsibilityEntry[] | { results: DocumentResponsibilityEntry[] }
    >(
      `${API_BASE}/document/${documentId}/responsibility-entries/assign-familiarizers/`,
      payload,
    )

    return data
  }

  /** Подтвердить ознакомление по своей записи */
  static async acknowledgeDocumentFamiliarizer(
    documentId: string,
    entryId: number,
  ): Promise<DocumentResponsibilityEntry> {
    const { data } = await axios.post<DocumentResponsibilityEntry>(
      `${API_BASE}/document/${documentId}/responsibility-entries/${entryId}/acknowledge/`,
    )

    return data
  }
}
